import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MailCheck, Loader2, RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '../../lib/auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toaster';

export function VerifyEmailScreen() {
  const { email, emailVerified, sendVerificationEmail, reloadUser, signOut, status } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  // Poll so the page resolves itself once the user clicks the confirmation
  // link in the email, without needing a manual reload.
  useEffect(() => {
    if (emailVerified) return;
    const id = window.setInterval(() => {
      void reloadUser();
    }, 4000);
    return () => window.clearInterval(id);
  }, [emailVerified, reloadUser]);

  useEffect(() => {
    if (status === 'signedOut') {
      navigate('/login', { replace: true });
      return;
    }
    if (emailVerified) {
      toast('Email terverifikasi', { body: 'Selamat datang di Bimbingan.', tone: 'success' });
      navigate('/', { replace: true });
    }
  }, [status, emailVerified, navigate, toast]);

  const resend = async () => {
    setSending(true);
    try {
      await sendVerificationEmail();
      toast('Email verifikasi dikirim', { body: 'Periksa kotak masuk Anda.', tone: 'success' });
    } catch {
      toast('Gagal mengirim', { body: 'Coba lagi dalam beberapa saat.', tone: 'error' });
    } finally {
      setSending(false);
    }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      await reloadUser();
      if (!emailVerified) {
        toast('Belum terverifikasi', {
          body: 'Klik tautan pada email yang kami kirimkan.',
          tone: 'info',
        });
      }
    } finally {
      setChecking(false);
    }
  };

  if (status === 'signedOut') {
    return null;
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[460px] flex-col justify-center px-6 pb-10 pt-[max(env(safe-area-inset-top),3rem)]">
      <div className="card p-6 text-center">
        <span className="mx-auto grid size-14 place-items-center rounded-none bg-accent-soft text-accent-strong">
          <MailCheck className="size-7" />
        </span>
        <h1 className="display mt-4 text-[20px] font-bold text-ink">Verifikasi email Anda</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-muted">
          Kami telah mengirim tautan konfirmasi ke{' '}
          <span className="font-semibold text-ink">{email ?? 'email Anda'}</span>. Klik tautan
          tersebut untuk mengaktifkan akun, lalu kembali ke halaman ini.
        </p>

        <div className="mt-6 flex flex-col gap-2.5">
          <Button block onClick={checkNow} disabled={checking}>
            {checking ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            {checking ? 'Memeriksa…' : 'Saya sudah verifikasi'}
          </Button>
          <Button block variant="outline" onClick={resend} disabled={sending}>
            {sending ? <Loader2 className="size-4 animate-spin" /> : null}
            Kirim ulang email
          </Button>
          <Button block variant="ghost" onClick={() => void signOut()} className="text-[13px]">
            <LogOut className="size-4" />
            Gunakan akun lain
          </Button>
        </div>
      </div>
    </div>
  );
}
